import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import {action, makeAutoObservable, observable} from "mobx";
import {SocialNetwork} from "data/enums";
import {share} from "data/utils";
import type {IShareImage, IShareStore} from "data/stores/share/share.store";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IUserStore} from "data/stores/user/user.store";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import type {useNavigate} from "react-router-dom";

interface IParams {
	navigate: ReturnType<typeof useNavigate>;
}

export interface IShareController extends ViewController<IParams> {
	get i18n(): ILocalizationStore;
	get isOpenShareModal(): boolean;
	get shareImagePath(): IShareImage;
	get isMobileShareVisible(): boolean;

	shareViaFB: () => void;
	shareViaTW: () => void;
	shareViaMobile: () => void;

	closeShareModal: () => void;
}

@injectable()
export class ShareController implements IShareController {
	@observable private _isNarrowScreen: boolean = false;
	@observable private _navigate: undefined | IParams["navigate"];

	get i18n() {
		return this._i18nStore;
	}

	get isOpenShareModal() {
		return this._shareStore.isOpenShareModal;
	}

	get roundId() {
		return this._shareStore.roundId;
	}

	get round() {
		if (!this.roundId) {
			return null;
		}

		return this._roundStore.getRoundById(this.roundId);
	}

	get shareImagePath() {
		return this._shareStore.shareImagePath;
	}

	get isMobileShareVisible() {
		return "share" in navigator && this._isNarrowScreen;
	}

	private get narrowScreenWatcher() {
		return window.matchMedia("(max-width: 960px)");
	}

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.ShareStore) public _shareStore: IShareStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore
	) {
		makeAutoObservable(this);
	}

	private share(socialNetwork: SocialNetwork) {
		share({
			socialNetwork,
			userId: this._userStore.user?.id || 0,
			roundId: this.roundId || 0,
			message: this.i18n.t(
				"pinpoint.modal.share.message",
				`I’ve made my Ryder Cup Pinpoint picks for the {{sessionName}} and entered the draw for tickets to the Ryder Cup 2025! Take me on at`,
				{
					sessionName: this.round?.name,
				}
			),
		});
	}

	shareViaFB = () => this.share(SocialNetwork.Facebook);

	shareViaTW = () => this.share(SocialNetwork.Twitter);

	shareViaMobile = () => this.share(SocialNetwork.Mobile);

	closeShareModal = () => {
		this._shareStore.closeShareModal();

		if (this._navigate) {
			this._navigate("/");
		}
	};

	@action private updateNarrowScreenFlag = () => {
		this._isNarrowScreen = this.narrowScreenWatcher.matches;
	};

	dispose(): void {
		this.narrowScreenWatcher?.removeEventListener("change", this.updateNarrowScreenFlag);
	}

	init(param: IParams): void {
		this._navigate = param.navigate;

		this.updateNarrowScreenFlag();
		this.narrowScreenWatcher?.addEventListener("change", this.updateNarrowScreenFlag);
	}
}
